"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Play, Sparkles, CircleCheck as CheckCircle2, Star } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Hero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const tasks = [{
    id: 1,
    column: "todo",
    title: "Assess oak health",
    tag: "Arbor",
    tagColor: "bg-orange-500/15 text-orange-700",
    assignee: "MK"
  }, {
    id: 2,
    column: "todo",
    title: "Pruning schedule",
    tag: "Maintenance",
    tagColor: "bg-stone-500/15 text-stone-700",
    assignee: "AL"
  }, {
    id: 3,
    column: "progress",
    title: "Site safety audit",
    tag: "Safety",
    tagColor: "bg-emerald-700/15 text-emerald-800",
    assignee: "JD"
  }, {
    id: 4,
    column: "progress",
    title: "Stump removal plan",
    tag: "Removal",
    tagColor: "bg-stone-600/15 text-stone-800",
    assignee: "SR"
  }, {
    id: 5,
    column: "done",
    title: "Canopy restoration",
    tag: "Restoration",
    tagColor: "bg-emerald-800/20 text-emerald-900",
    assignee: "MK"
  }];
  const activity = [{
    id: 1,
    name: "Mark",
    action: "completed",
    target: "Tree Inspection",
    time: "2m ago",
    initials: "MK"
  }, {
    id: 2,
    name: "Anna",
    action: "reviewed",
    target: "Site Safety",
    time: "8m ago",
    initials: "AL"
  }, {
    id: 3,
    name: "John",
    action: "scheduled",
    target: "Pruning Job",
    time: "12m ago",
    initials: "JD"
  }];
  const avatars = [{
    id: 1,
    initials: "MK",
    color: "bg-emerald-800"
  }, {
    id: 2,
    initials: "AL",
    color: "bg-stone-600"
  }, {
    id: 3,
    initials: "JD",
    color: "bg-orange-600"
  }];
  return <section data-section-id="73" id="hero" className="relative overflow-hidden bg-background text-foreground">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10" style={{
      background: "radial-gradient(ellipse 80% 60% at top right, hsla(30, 85%, 50%, 0.12), transparent 60%)"
    }} />
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 opacity-[0.04]" style={{
      backgroundImage: "radial-gradient(circle, hsl(30 15% 25%) 1px, transparent 1px)",
      backgroundSize: "24px 24px"
    }} />

      <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24 lg:py-28">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-20">
          {}
          <div ref={leftRef} className={`lg:col-span-6 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <Badge variant="outline" className="mb-8 gap-2 rounded-lg border-border bg-card px-3 py-1.5 text-[11px] font-medium uppercase tracking-widest text-muted-foreground shadow-sm">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-lg bg-accent opacity-75"></span>
                <span className="relative inline-flex size-2 rounded-lg bg-accent"></span>
              </span>
              <span className="text-foreground">NEW</span>
              <span aria-hidden="true">·</span>
              <span>Emergency tree services available</span>
            </Badge>

            {}
            <h1 className="font-default font-bold text-foreground" style={{
            fontSize: "clamp(48px, 7vw, 88px)",
            letterSpacing: "-0.03em",
            lineHeight: "1.02"
          }}>
              Professional tree care for your{" "}
              <span className="bg-clip-text text-transparent" style={{
              backgroundImage: "linear-gradient(120deg, hsl(30 85% 50%), hsl(30 15% 25%))"
            }}>
                landscape
              </span>
              .
            </h1>

            {}
            <p className="mt-7 max-w-[520px] text-[19px] text-muted-foreground" style={{
            lineHeight: 1.55
          }}>
              Expert arborists dedicated to the health, safety, and beauty of your trees. From pruning to removal, we handle it all with precision.
            </p>

            {}
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/GetQuotePage">
                <Button size="lg" className="group h-12 rounded-lg px-6 text-[15px] font-medium shadow-md hover:shadow-lg bg-primary text-primary-foreground">
                  Get a Free Quote
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </Link>
              <Link to="/OurExpertisePage">
                <Button size="lg" variant="ghost" className="group h-12 gap-3 rounded-lg px-4 text-[15px] font-medium text-foreground hover:bg-accent/40">
                  <span className="flex size-8 items-center justify-center rounded-lg bg-accent">
                    <Play className="size-3.5 fill-accent-foreground text-accent-foreground" />
                  </span>
                  Our Expertise
                </Button>
              </Link>
            </div>

            {}
            <div className="mt-10 flex items-center gap-3">
              <div className="flex -space-x-2">
                {avatars.map((a, i) => <SafeHydrate><Avatar key={a.id} data-index={i} className="size-7 border-2 border-background">
                    <AvatarFallback className={`${a.color} text-[10px] font-semibold text-white`}>
                      {a.initials}
                    </AvatarFallback>
                  </Avatar></SafeHydrate>)}
              </div>
              <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
                <Star className="size-3.5 fill-accent text-accent" />
                <span className="font-medium text-foreground">5.0</span>
                <span>from 500+ satisfied clients</span>
              </div>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`relative lg:col-span-6 transition-all duration-1000 ease-out ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative mx-auto w-full max-w-[640px]">
              {}
              <div aria-hidden="true" className="absolute -inset-8 -z-10 blur-3xl" style={{
              background: "radial-gradient(circle at 60% 40%, hsla(30, 85%, 50%, 0.35), hsla(30, 15%, 25%, 0.2) 40%, transparent 70%)"
            }} />

              {}
              <div className="relative" style={{
              animation: "tasklyFloat 4s ease-in-out infinite"
            }}>
                {}
                <div className="relative rounded-2xl border border-border bg-card shadow-2xl" style={{
                transform: "rotate(2deg)"
              }}>
                  {}
                  <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1.5">
                        <span className="size-2.5 rounded-lg bg-stone-400/60" />
                        <span className="size-2.5 rounded-lg bg-emerald-600/60" />
                        <span className="size-2.5 rounded-lg bg-orange-600/60" />
                      </div>
                      <span className="ml-3 text-xs font-medium text-muted-foreground">
                        Arborist Schedule · Weekly
                      </span>
                    </div>
                    <Sparkles className="size-3.5 text-muted-foreground" />
                  </div>

                  {}
                  <div className="grid grid-cols-3 gap-3 p-4">
                    {[{
                    key: "todo",
                    label: "Pending",
                    count: 2,
                    dot: "bg-muted-foreground"
                  }, {
                    key: "progress",
                    label: "Active",
                    count: 2,
                    dot: "bg-emerald-700"
                  }, {
                    key: "done",
                    label: "Done",
                    count: 1,
                    dot: "bg-orange-600"
                  }].map((col, i) => <div key={col.key} data-index={i} className="flex flex-col gap-2">
                        <div className="mb-1 flex items-center justify-between px-1">
                          <div className="flex items-center gap-1.5">
                            <span className={`size-1.5 rounded-lg ${col.dot}`} />
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                              {col.label}
                            </span>
                          </div>
                          <span className="text-[11px] text-muted-foreground">
                            {col.count}
                          </span>
                        </div>
                        {tasks.filter(t => t.column === col.key).map((task, j) => <div key={task.id} data-index={j} className="rounded-lg border border-border bg-background p-2.5 shadow-sm">
                              <span className={`mb-2 inline-flex rounded-md px-1.5 py-0.5 text-[9px] font-medium ${task.tagColor}`}>
                                {task.tag}
                              </span>
                              <p className="text-[12px] font-medium leading-snug text-foreground">
                                {task.title}
                              </p>
                              <div className="mt-2.5 flex items-center justify-between">
                                <SafeHydrate><Avatar className="size-5">
                                  <AvatarFallback className="bg-secondary text-[9px] font-semibold text-secondary-foreground">
                                    {task.assignee}
                                  </AvatarFallback>
                                </Avatar></SafeHydrate>
                                <span className="text-[10px] text-muted-foreground">
                                  Today
                                </span>
                              </div>
                            </div>)}
                      </div>)}
                  </div>
                </div>

                {}
                <div className="absolute -bottom-8 -left-6 hidden w-[260px] rounded-xl border border-border bg-card p-3.5 shadow-xl sm:block" style={{
                transform: "rotate(-3deg)"
              }}>
                  <div className="mb-3 flex items-center justify-between">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Recent Updates
                    </span>
                    <span className="size-1.5 rounded-lg bg-emerald-600" />
                  </div>
                  <div className="space-y-2.5">
                    {activity.map((a, i) => <div key={a.id} data-index={i} className="flex items-start gap-2.5">
                        <SafeHydrate><Avatar className="size-6 shrink-0">
                          <AvatarFallback className="bg-secondary text-[9px] font-semibold text-secondary-foreground">
                            {a.initials}
                          </AvatarFallback>
                        </Avatar></SafeHydrate>
                        <div className="min-w-0 flex-1">
                          <p className="text-[11px] leading-snug text-foreground">
                            <span className="font-semibold">{a.name}</span>{" "}
                            <span className="text-muted-foreground">{a.action}</span>{" "}
                            <span className="font-medium">{a.target}</span>
                          </p>
                          <span className="text-[10px] text-muted-foreground">
                            {a.time}
                          </span>
                        </div>
                      </div>)}
                  </div>
                </div>

                {}
                <div className="absolute -top-4 -right-2 hidden items-center gap-2.5 rounded-lg border border-border bg-card px-3.5 py-2.5 shadow-xl sm:flex" style={{
                transform: "rotate(3deg)"
              }}>
                  <span className="flex size-7 items-center justify-center rounded-lg bg-accent">
                    <CheckCircle2 className="size-4 text-accent-foreground" />
                  </span>
                  <div>
                    <p className="text-[11px] font-semibold text-foreground">
                      Job Completed
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      Tree Inspection
                    </p>
                  </div>
                </div>
              </div>

              {}
              <div aria-hidden="true" className="mx-auto mt-4 h-12 w-3/4 blur-xl opacity-40" style={{
              background: "radial-gradient(ellipse at center, hsla(30, 85%, 50%, 0.5), transparent 70%)"
            }} />

              {}
              <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-24 lg:hidden" style={{
              background: "linear-gradient(to bottom, transparent, hsl(35 30% 98%))"
            }} />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes tasklyFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
      `}</style>
    </section>;
}